import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Chip } from '@/components/Chip';
import { BodyBadge } from '@/components/BodyBadge';
import { Icon } from '@/components/Icon';
import { fetchCorpusStats, fetchFollowedTowns, fetchSignalMatches, fetchSignals } from '@/lib/api';
import { EVENT_TYPES, type SignalMatch } from '@/lib/signals';
import { useTier } from '@/session/SessionProvider';
import { can } from '@/lib/tier';
import { SignalHistory } from '@/features/feed/SignalHistory';
import { SignalSheet, type SheetSeed } from '@/features/signals/SignalSheet';
import { sayCountries, sayTowns } from '@/lib/corpus';
import './SignalFeed.css';

/**
 * Signals — the pro working surface.
 *
 * The same layout system as My Towns at full density: the identical chip idiom on top, a
 * denser row set below. New matches from the user's Signals, across every jurisdiction.
 *
 * The chip row does two different things, deliberately:
 *   clicking the chip   filters the feed to that Signal
 *   clicking its count  opens that Signal's full all-time match history in an overlay
 *
 * "Manage signals" leads to the saved-definition CRUD list at /signals/manage. That list is not
 * a nav destination — it is reached from here, which is why this surface owns the link.
 */
export function SignalFeed() {
  const tier = useTier();
  const [activeSignal, setActiveSignal] = useState<string | null>(null);
  const [historySignal, setHistorySignal] = useState<string | null>(null);
  const [sheet, setSheet] = useState<SheetSeed | null>(null);

  const signalsQuery = useQuery({ queryKey: ['signals', tier], queryFn: fetchSignals });
  const matchesQuery = useQuery({
    queryKey: ['signal-feed', tier, activeSignal],
    queryFn: () => fetchSignalMatches(activeSignal ?? undefined),
    placeholderData: (previous) => previous,
  });

  const townsQuery = useQuery({ queryKey: ['towns', tier], queryFn: fetchFollowedTowns });
  const statsQuery = useQuery({ queryKey: ['corpus'], queryFn: fetchCorpusStats });

  const signals = signalsQuery.data ?? [];
  const matches = matchesQuery.data ?? [];

  return (
    <div className="feed">
      <header className="feed__head">
        <div>
          <h1 className="feed__title">Signals</h1>
          <p className="feed__subtitle">
            New matches from your Signals, across every place we cover.
          </p>
        </div>
        <div className="feed__headActions">
          <button type="button" className="feed__manage" onClick={() => setSheet({})}>
            New signal
          </button>
          <Link to="/signals/manage" className="feed__manage">
            Manage signals
          </Link>
        </div>
      </header>

      <div className="feed__chips u-scroller" role="tablist" aria-label="Signals">
        <Chip
          label="All signals"
          selected={activeSignal === null}
          onClick={() => setActiveSignal(null)}
        />
        {signals.map((signal) => (
          <Chip
            key={signal.id}
            icon="signals"
            label={signal.name}
            count={signal.total}
            countLabel={`Open the full match history for ${signal.name}`}
            selected={activeSignal === signal.name}
            onClick={() =>
              setActiveSignal((current) => (current === signal.name ? null : signal.name))
            }
            onCountClick={() => setHistorySignal(signal.name)}
          />
        ))}
      </div>

      {matchesQuery.isLoading ? (
        <p className="feed__empty">Loading matches…</p>
      ) : matches.length === 0 ? (
        <p className="feed__empty">
          No new matches for this Signal yet. Its full history is on the count badge.
        </p>
      ) : (
        <>
          {/* One row set, two presentations: cards on a phone, a table once there is room.
              Both render the same data in the same order — a table that becomes a card list
              is easier to keep honest than two separate components. */}
          <div className="feed__rows" role="table" aria-label="Signal matches">
            <div className="feed__headerRow" role="row">
              <span role="columnheader">Where</span>
              <span role="columnheader">Body &amp; date</span>
              <span role="columnheader">Matched passage</span>
              <span role="columnheader">Event</span>
              <span role="columnheader">Signal</span>
            </div>
            {matches.map((match) => (
              <MatchRow key={match.id} match={match} canTrack={can(tier, 'legislativeHistory')} />
            ))}
          </div>
        </>
      )}

      {sheet && (
        <SignalSheet
          seed={sheet}
          followedTownCount={(townsQuery.data ?? []).length}
          corpusSummary={
            statsQuery.data
              ? `${sayTowns(statsQuery.data)} towns · ${sayCountries(statsQuery.data)} countries`
              : 'every place'
          }
          onClose={() => setSheet(null)}
        />
      )}

      {historySignal && (
        <SignalHistory
          signalName={historySignal}
          total={signals.find((s) => s.name === historySignal)?.total ?? 0}
          onClose={() => setHistorySignal(null)}
        />
      )}
    </div>
  );
}

function MatchRow({ match, canTrack }: { match: SignalMatch; canTrack: boolean }) {
  return (
    <article className="feed__row" role="row">
      <span className="feed__cell feed__where" role="cell">
        <strong className="feed__city">
          {match.city}, {match.region}
        </strong>
        <span className="feed__pop">pop. {match.population}</span>
      </span>

      <span className="feed__cell feed__when" role="cell">
        <BodyBadge body={match.body} short />
        <span className="feed__date">{match.date}</span>
      </span>

      <span className="feed__cell feed__passage" role="cell">
        <p className="feed__quote">
          {match.passage.before}
          {/* The server sends the match position, not markup — nothing here injects HTML. */}
          <mark className="feed__mark">{match.passage.hit}</mark>
          {match.passage.after}
        </p>
        {match.entities.length > 0 && (
          <ul className="feed__entities">
            {match.entities.map((entity) => (
              <li key={entity} className="feed__entity">
                {entity}
              </li>
            ))}
          </ul>
        )}
        <span className="feed__meta">
          {match.num && <span className="feed__num">{match.num}</span>}
          {/* Vote is read from the pipeline's own field — non-null means it was voted on. */}
          {match.vote && <span className="feed__vote">{match.vote}</span>}
        </span>
      </span>

      <span className="feed__cell feed__event" role="cell">
        <span className="feed__eventChip" data-event={match.eventType}>
          {EVENT_TYPES[match.eventType].label}
        </span>
      </span>

      <span className="feed__cell feed__provenance" role="cell">
        <span className="feed__signalName">
          <Icon name="signals" /> {match.signalName}
        </span>
        {match.num &&
          (match.recordId ? (
            <Link to="/legislative-history" className="feed__link">
              In this record
            </Link>
          ) : canTrack ? (
            <button type="button" className="feed__track">
              Track {match.num}
            </button>
          ) : null)}
      </span>
    </article>
  );
}
